import React, { useState, forwardRef, useImperativeHandle } from "react";
import { Dialog, CircularProgress } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import QuestionMarkIcon from "@mui/icons-material/QuestionMark";
import toast from "react-hot-toast";

import SoftBox from "../../components/SoftBox";
import SoftTypography from "../../components/SoftTypography";
import SoftButton from "../../components/SoftButton";

const ArchivePlayer = forwardRef(({
  isLoggedInUser,
  archiveUser,
  archiveStatus,
  user,
  isFirstAdmin,
  // When true, suppresses the default inline button so the parent (kebab menu)
  // can render its own trigger. The dialog still renders.
  hideTrigger = false,
}, ref) => {
  const [archivePlayerDialog, setArchivePlayerDialog] = useState(false);
  const [loading, setLoading] = useState(false);

  const openArchivePlayerDialog = () => setArchivePlayerDialog(true);
  const closeArchivePlayerDialog = () => setArchivePlayerDialog(false);

  useImperativeHandle(ref, () => ({ open: openArchivePlayerDialog }), []);

  const archiveUserHandler = async () => {
    setLoading(true);
    const willArchive = !user.isArchived;
    try {
      await archiveUser(user.userId, willArchive);
      closeArchivePlayerDialog();
      // archiveUser() already patches Redux + this table's local state, so
      // there's no reason to wait on a full page reload (which used to run
      // here on every single archive/unarchive) just to see the change.
      toast.success(
        `${user.firstName} ${user.lastName} ${willArchive ? "archived" : "unarchived"}`,
      );
    } catch (error) {
      console.log("Error arhiveUser", error.response);
      toast.error("Unable to arhive the user");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {!hideTrigger && (
      <SoftButton
        size="small"
        color="primary"
        onClick={!isFirstAdmin ? openArchivePlayerDialog : undefined}
        sx={{
          width: "auto",
          maxWidth: "100px",
          height: "32px",
          borderRadius: "8px",
          textTransform: "none",
          marginRight: "0.5rem",
          border: "1px solid #E4E7EC",
          background: "#FFFFFF !important",
          color: "#475467",
          boxShadow: "none !important",
          opacity: "1 !important",
          fontFamily: "DM Sans",
          fontSize: "12px",
          fontWeight: 500,
          padding: "0 12px",
          "&:hover": { background: "#F9FAFB !important", color: "#191D23" },
        }}
      >
        {archiveStatus ? "Archived" : "Archive"}
      </SoftButton>
      )}
      {archivePlayerDialog && (
        <Dialog
          open={archivePlayerDialog}
          onClose={closeArchivePlayerDialog}
          sx={{
            "& .MuiPaper-root": {
              padding: { mini: "1rem", md: "1.5rem 1.7rem" },
              borderRadius: "24px",
              background:
                "linear-gradient(180deg, rgba(255,255,255,0.70) 0%, rgba(255,255,255,0.60) 100%)",
              boxShadow:
                "0px 24px 48px -8px rgba(16,24,40,0.18), inset 0px 1px 0px 0px rgba(255,255,255,0.7)",
              backdropFilter: "blur(18px) saturate(160%)",
              width: "100%",
              maxWidth: "700px",
              border: "1px solid rgba(255,255,255,0.6)",
            },
          }}
        >
          <SoftBox display="flex" justifyContent="flex-end">
            <SoftButton
              variant="outlined"
              color="dark"
              onClick={closeArchivePlayerDialog}
              sx={{
                "& svg": { fontSize: "1.25rem !important" },
                borderRadius: "50%",
                minWidth: "max-content",
                minHeight: "max-content",
                padding: "0.25rem",
                color: "#475467",
              }}
            >
              <CloseRoundedIcon />
            </SoftButton>
          </SoftBox>
          <SoftBox
            display="flex"
            flexDirection="column"
            alignItems="center"
            mb="60px"
          >
            <QuestionMarkIcon
              sx={{ fontSize: "100px !important", color: "#0F172A" }}
            />
            <SoftTypography
              component="h4"
              sx={{
                fontWeight: 700,
                color: "#0F172A",
                fontSize: 24,
                marginBottom: "20px",
                marginTop: "20px",
                textAlign: "center",
              }}
            >
              Are you sure you want to {archiveStatus ? "unarchive" : "archive"}{" "}
              {user.firstName} {user.lastName}?
            </SoftTypography>
            <SoftTypography
              component="p"
              sx={{
                fontWeight: 500,
                color: "#475467",
                fontSize: 18,
                textAlign: "center",
              }}
            >
              Confirming this Action will update your Subscription
            </SoftTypography>
          </SoftBox>
          <SoftBox display="flex" justifyContent="space-between">
            <SoftButton
              color="dark"
              variant="outlined"
              onClick={closeArchivePlayerDialog}
              sx={{
                textTransform: "none",
                borderRadius: "4px",
                color: "#475467",
              }}
            >
              Cancel
            </SoftButton>
            <SoftButton
              variant="contained"
              color="primary"
              onClick={archiveUserHandler}
              disabled={loading}
              sx={{
                mx: 2,
                textTransform: "none",
                borderRadius: "4px",
                color: "primary.contrastText",
              }}
            >
              {archiveStatus ? "Unarchive" : "Archive"} Player{" "}
              {loading && (
                <CircularProgress
                  color="inherit"
                  size="1rem"
                  className="ml-2"
                />
              )}
            </SoftButton>
          </SoftBox>
        </Dialog>
      )}
    </>
  );
});

export default ArchivePlayer;
